/**
 * repositories/usersRepository.ts
 * Users table data access layer
 *
 * REFERENCE IMPLEMENTATION - Copy these patterns to all repositories
 *
 * Architecture: WU1.5 Task 2 - Repository Pattern Reference
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toWire, toPatch } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type { User, UserInsert, UserUpdate, UserStatus } from '@/types/supabase/User';

// PATTERN: Import wire types from generated schema
type UserRow = Database['public']['Tables']['users']['Row'];
type UserInsertRow = Database['public']['Tables']['users']['Insert'];
type UserUpdateRow = Database['public']['Tables']['users']['Update'];

/**
 * ============================================================================
 * REPOSITORY RULES (copy these to all repositories)
 * ============================================================================
 *
 * 1. **Select only needed columns**
 *    - Use .select('id, email, full_name, ...') for performance
 *    - Can alias columns: .select('id, createdAt:created_at')
 *    - Still run through toDomain() for consistency
 *
 * 2. **Always map snake⇄camel at the boundary**
 *    - Database returns snake_case (wire types)
 *    - Repository returns camelCase (domain types)
 *    - Use toDomain() for reads, toWire() for inserts, toPatch() for updates
 *
 * 3. **undefined = omit on update; null = clear column**
 *    - toPatch() strips undefined keys automatically
 *    - Passing null will set column to NULL in database
 *    - This prevents accidental data loss on partial updates
 *
 * 4. **Normalize inputs**
 *    - Emails: trim() + toLowerCase() before queries
 *    - Ensures consistent lookups and storage
 *
 * 5. **Error handling**
 *    - Throw on Supabase errors (let caller handle)
 *    - Return null for "not found" on single lookups
 *    - Return empty array for "not found" on list queries
 *
 * ============================================================================
 */

/**
 * Users Repository
 *
 * Provides type-safe data access for users table with centralized mapping
 * between database snake_case and application camelCase.
 */
export const usersRepository = {
  /**
   * Get user by ID
   *
   * PATTERN: Single record lookup with nullable return
   *
   * @param id - User ID (UUID)
   * @returns Promise<User | null> - User if found, null otherwise
   */
  async getById(id: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .maybeSingle<UserRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<User>(data);
  },

  /**
   * Get user by email address
   *
   * PATTERN: Email normalization + case-insensitive lookup
   *
   * @param email - Email address (will be normalized: trim + lowercase)
   * @returns Promise<User | null> - User if found, null otherwise
   */
  async getByEmail(email: string): Promise<User | null> {
    // PATTERN: Normalize email inputs for consistent lookups
    const normalizedEmail = email.trim().toLowerCase();

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .ilike('email', normalizedEmail)
      .maybeSingle<UserRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<User>(data);
  },

  /**
   * Get multiple users by IDs
   *
   * PATTERN: Batch fetch for efficiency
   *
   * @param ids - Array of user IDs
   * @returns Promise<User[]> - Array of found users (may be fewer than requested)
   */
  async getByIds(ids: string[]): Promise<User[]> {
    if (ids.length === 0) return [];

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .in('id', ids);

    if (error) throw error;
    if (!data) return [];

    return data.map(row => toDomain<User>(row));
  },

  /**
   * Check if email exists
   *
   * PATTERN: Lightweight existence check without fetching data
   *
   * @param email - Email address to check (will be normalized)
   * @returns Promise<boolean> - True if email exists
   */
  async existsByEmail(email: string): Promise<boolean> {
    const normalizedEmail = email.trim().toLowerCase();

    const { data, error } = await supabase
      .from('users')
      .select('id')
      .ilike('email', normalizedEmail)
      .maybeSingle();

    if (error) throw error;
    return data !== null;
  },

  /**
   * List users with pagination and filtering
   *
   * PATTERN: Paginated list with total count for UI
   *
   * @param opts - List options
   * @param opts.status - Filter by user status (optional)
   * @param opts.limit - Max results per page (default: 50)
   * @param opts.offset - Starting offset (default: 0)
   * @returns Promise<{ items: User[], total: number }> - Paginated results with total count
   */
  async list(opts: {
    status?: UserStatus;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ items: User[]; total: number }> {
    const { status, limit = 50, offset = 0 } = opts;

    // PATTERN: Build query with optional filters
    let query = supabase
      .from('users')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    // PATTERN: Pagination with range (PostgreSQL LIMIT/OFFSET)
    const { data, error, count } = await query.range(offset, offset + limit - 1);

    if (error) throw error;
    if (!data) return { items: [], total: 0 };

    return {
      items: data.map(row => toDomain<User>(row)),
      total: count ?? 0
    };
  },

  /**
   * Create new user
   *
   * PATTERN: Insert with wire type conversion
   *
   * @param userData - User data (camelCase domain type)
   * @returns Promise<User> - Created user
   */
  async create(userData: UserInsert): Promise<User> {
    // PATTERN: Convert domain type (camelCase) to wire type (snake_case)
    const wireData = toWire<UserInsertRow>(userData);

    const { data, error } = await supabase
      .from('users')
      .insert(wireData)
      .select('*')
      .single<UserRow>();

    if (error) throw error;
    if (!data) throw new Error('Insert succeeded but no data returned');

    return toDomain<User>(data);
  },

  /**
   * Update user
   *
   * PATTERN: Partial update with undefined stripping
   *
   * @param id - User ID
   * @param updates - Partial user updates (camelCase domain type)
   * @returns Promise<User> - Updated user
   */
  async update(id: string, updates: UserUpdate): Promise<User> {
    // PATTERN: toPatch strips undefined (omit), keeps null (clear column)
    const wireUpdates = toPatch<UserUpdateRow>(updates);

    const { data, error } = await supabase
      .from('users')
      .update(wireUpdates)
      .eq('id', id)
      .select('*')
      .single<UserRow>();

    if (error) throw error;
    if (!data) throw new Error('Update succeeded but no data returned');

    return toDomain<User>(data);
  },

  /**
   * Delete user
   *
   * PATTERN: Delete by ID with void return
   *
   * @param id - User ID to delete
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Get user status and onboarding statistics
   *
   * PATTERN: Specialized query for admin dashboard statistics
   * Fetches only needed columns (status, onboarding_completed) for efficiency
   *
   * @returns Promise<UserStats> - Aggregated user statistics
   */
  async getStatusStats(): Promise<{
    total: number;
    byStatus: Record<UserStatus, number>;
    onboardingCompleted: number;
  }> {
    const { data, error } = await supabase
      .from('users')
      .select('status, onboarding_completed');

    if (error) throw error;

    const users = data || [];
    const total = users.length;

    // Aggregate by status
    const byStatus = users.reduce((acc, user) => {
      const status = user.status as UserStatus;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<UserStatus, number>);

    // Count onboarding completion
    const onboardingCompleted = users.filter(u => u.onboarding_completed).length;

    return {
      total,
      byStatus,
      onboardingCompleted
    };
  }
};
